import { useParams, useSearchParams, Link } from "react-router-dom";
import TenantLogo from "@/components/TenantLogo";
import OtpLogin from "@/components/OtpLogin";
import { useOpenDestination } from "@/lib/signIn";
import { TEST_NUMBERS } from "@/lib/testAccounts";
import { useTenant } from "./TenantRoot";

/**
 * /t/<slug>/login: the sign-in of this tenant's site and of its mobile app.
 * One screen for everyone. After the code is verified the server says what the
 * number is here (customer, seller, this business's admin, or the platform's
 * Super Admin) and the person is taken there. Nobody has to know or type a
 * different address to reach their area.
 */
export default function TenantLogin() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const { tenant } = useTenant();
  const open = useOpenDestination();
  const base = `/t/${slug}`;
  const appName = tenant.branding?.app_name || tenant.business_name;

  return (
    <div className="pwa-shell pwa-shell--plain brand-bg brand-body flex flex-col md:justify-center" data-testid="tenant-login-page">
      <div className="auth-card w-full max-w-md mx-auto px-6 pt-12 pb-8">
        <Link to={base} className="inline-flex items-center gap-3 min-w-0 mb-6">
          <TenantLogo tenant={tenant} className="h-12 w-12 rounded-xl text-lg" />
        </Link>
        <h1 className="brand-heading font-bold text-3xl tracking-tight leading-tight break-words">Welcome to<br/>{appName}</h1>
        <p className="brand-text-muted text-sm mt-2">Sign in with your mobile number.</p>

        <div className="mt-8">
          {/* `next` is honoured only for a customer, and only inside this tenant (see useOpenDestination). */}
          <OtpLogin key={slug} slug={slug} look="brand" testid="tenant-login" withName
            onSignedIn={(session) => open(session, { next: sp.get("next") })} testNumbers={TEST_NUMBERS} />
        </div>

        <p className="text-xs brand-text-muted mt-6" data-testid="tenant-login-everyone">
          One sign-in for customers, sellers and the business. Your number decides where you go.
        </p>
        <p className="text-[11px] brand-text-muted mt-3 leading-relaxed">
          By continuing you agree to the{" "}
          <Link to={`${base}/terms`} className="underline">Terms & Conditions</Link> and{" "}
          <Link to={`${base}/privacy-policy`} className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
